/**
 * ObstructionCard Component
 *
 * Compact card used in the obstructions sidebar list. Pattern mirrors
 * `NoteCard` — status dot, title, type/severity badges, road, planned
 * date range, contractor company. Clicking the card selects the
 * obstruction and opens the details panel.
 */
import { memo, useCallback, type CSSProperties } from 'react';
import { Badge, Card } from '@mantine/core';
import { IconBuilding, IconCalendar, IconRoad } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '@/store';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import {
  OBSTRUCTION_STATUS_COLORS,
  SEVERITY_COLORS,
} from '@/constants/obstructions';
import { ICON_SIZE_TAB } from '@/constants/ui';
import type { ObstructionListItem } from '@/types/obstructions';
import styles from './ObstructionCard.module.css';

interface ObstructionCardProps {
  readonly obstruction: ObstructionListItem;
  readonly isSelected?: boolean;
}

export const ObstructionCard = memo(function ObstructionCard({
  obstruction,
  isSelected = false,
}: ObstructionCardProps) {
  const { t } = useTranslation();
  const { formatDateShort } = useLocalizedDate();

  const selectObstruction = useGeoMarkupStore(
    (state) => state.obstructions.selectObstruction
  );
  const openDetails = useGeoMarkupStore(
    (state) => state.obstructions.openDetails
  );

  const handleClick = useCallback(() => {
    selectObstruction(obstruction.id);
    openDetails();
  }, [selectObstruction, openDetails, obstruction.id]);

  const cardClassName = isSelected
    ? `${styles.card} ${styles.selected}`
    : styles.card;

  const statusStyle = {
    '--obstruction-status-color': OBSTRUCTION_STATUS_COLORS[obstruction.status],
  } as CSSProperties;

  const severityStyle = {
    '--obstruction-severity-color': SEVERITY_COLORS[obstruction.severity],
  } as CSSProperties;

  const dateRangeLabel = `${formatDateShort(obstruction.plannedStartDate)} – ${formatDateShort(obstruction.plannedEndDate)}`;

  return (
    <Card
      className={cardClassName}
      onClick={handleClick}
      padding="sm"
      withBorder
    >
      <div className={styles.titleRow}>
        <div className={styles.titleGroup}>
          <span
            className={styles.statusDot}
            style={statusStyle}
            aria-label={t(`obstructions.status.${obstruction.status}`)}
          />
          <span className={styles.title}>{obstruction.title}</span>
        </div>
      </div>

      <div className={styles.badgesRow}>
        <Badge size="sm" variant="light" color="gray">
          {t(`obstructions.type.${obstruction.type}`)}
        </Badge>
        <Badge
          size="sm"
          variant="filled"
          className={styles.severityBadge}
          style={severityStyle}
        >
          {t(`obstructions.severity.${obstruction.severity}`)}
        </Badge>
        <Badge size="sm" variant="outline" color="gray">
          {t(`obstructions.impact.${obstruction.impact}`)}
        </Badge>
      </div>

      {obstruction.location !== null && obstruction.location.length > 0 && (
        <div className={styles.metaRow}>
          <div className={styles.metaGroup}>
            <IconRoad
              size={ICON_SIZE_TAB}
              className={styles.metaIcon}
              aria-label={t('obstructions.card.location')}
            />
            <span className={styles.metaText}>{obstruction.location}</span>
          </div>
        </div>
      )}

      <div className={styles.metaRow}>
        <div className={styles.metaGroup}>
          <IconCalendar size={ICON_SIZE_TAB} className={styles.metaIcon} />
          <span className={styles.metaText}>{dateRangeLabel}</span>
        </div>
      </div>

      {obstruction.contractorCompany.length > 0 && (
        <div className={styles.contractorRow}>
          <div className={styles.metaGroup}>
            <IconBuilding size={ICON_SIZE_TAB} className={styles.metaIcon} />
            <span className={styles.metaText}>
              {obstruction.contractorCompany}
            </span>
          </div>
        </div>
      )}
    </Card>
  );
});
